import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';
import TextareaAutosize from 'react-textarea-autosize';

import Avatar from '../../Avatar';
import { AVATAR_SIZE_36 } from '../../../../constants/avatar-sizes';
import { getAccountName } from '../../../../util/account';
import Button from '../../Button';
import DotTrigger from '../../DotTrigger';
import DropdownMenu from '../../DropdownMenu';
import useRequestConfirm from '../../../../hooks/useRequestConfirm';
import RefreshingDate from './RefreshingDate';
import Label from '../../Label';

const propTypes = {
  accountFileComment: PropTypes.shape({
    id: PropTypes.string,
    message: PropTypes.string,
    account: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      avatar_photo: PropTypes.object,
    }),
  }),
  repliesOpen: PropTypes.bool,
  onReply: PropTypes.func,
  onViewReplies: PropTypes.func,
  onCommentUpdate: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  isOwner: PropTypes.bool,
  target: PropTypes.shape({
    fileCommentId: PropTypes.string,
    parentFileCommentId: PropTypes.string,
  }),
};

const FileCommentItem = ({
  accountFileComment,
  repliesOpen,
  onReply,
  onViewReplies,
  onCommentUpdate,
  onCommentDelete,
  user,
  isOwner,
  target,
}) => {
  const [editing, setEditing] = useState(false);
  const [updatedMessage, setUpdatedMessage] = useState(
    accountFileComment?.message ?? '',
  );
  const updatedMessageInput = useRef();
  const requestConfirm = useRequestConfirm();

  const classes = classNames({
    'file-comment-item': true,
    'file-comment-item-replies-open': repliesOpen,
    'is-target': target?.fileCommentId === accountFileComment?.id,
  });

  useEffect(() => {
    if (editing) {
      setUpdatedMessage(accountFileComment.message);
      updatedMessageInput.current?.focus();
    } else {
      setUpdatedMessage('');
    }
  }, [editing]);

  const canUpdate = user?.id === accountFileComment?.account?.id;
  const canDelete = canUpdate || isOwner;

  const onEditingSave = () => {
    onCommentUpdate(accountFileComment.id, updatedMessage);
    setEditing(false);
  };

  const onEditingCancel = () => {
    setUpdatedMessage(accountFileComment.message);
    setEditing(false);
  };

  return (
    <>
      <div className={classes} id={`comment-${accountFileComment.id}`}>
        <Avatar
          photo={accountFileComment.account?.avatar_photo}
          size={AVATAR_SIZE_36}
        />
        {editing ? (
          <div className="file-comment-item-wrapper">
            <Label labelSrOnly label="Update comment" id="comment-update" />
            <TextareaAutosize
              className="new-reply-input form-control"
              placeholder="Update your comment..."
              name="comment-update"
              id="comment-update"
              value={updatedMessage}
              onChange={event => {
                setUpdatedMessage(event.target.value);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.stopPropagation();
                  event.preventDefault();
                  onEditingSave();
                }

                if (event.keyCode === 27) {
                  // Escape key
                  event.stopPropagation();
                  event.preventDefault();
                  onEditingCancel();
                }
              }}
              ref={updatedMessageInput}
            />
            <span className="text-xs d-flex align-items-center mt-1 justify-content-end">
              <Button
                btnStyle="link"
                btnSize="xs"
                onClick={onEditingCancel}
                className="ml-auto text-danger"
              >
                Cancel
              </Button>
              <Button
                btnStyle="link"
                btnSize="xs"
                onClick={onEditingSave}
                className="ml-1"
              >
                Save
              </Button>
            </span>
          </div>
        ) : (
          <>
            <div className="file-comment-item-wrapper">
              <div className="file-comment-item-content">
                <div className="d-flex align-items-center mb-1">
                  <p className="file-comment-item-name mb-0">
                    {getAccountName(accountFileComment.account)}
                  </p>
                  <div className="dropdown-menu-dark">
                    {(canUpdate || canDelete) && (
                      <DropdownMenu
                        placement="bottom-end"
                        toggle={
                          <DotTrigger className="file-comment-item-dropdown ml-auto" />
                        }
                      >
                        {canUpdate && (
                          <button
                            className="dropdown-item"
                            onClick={() => setEditing(true)}
                          >
                            Edit comment
                          </button>
                        )}
                        {canDelete && onCommentDelete && (
                          <button
                            className="dropdown-item text-danger"
                            onClick={() => {
                              requestConfirm({
                                message: (
                                  <Fragment>
                                    <p className="text-lg text-extra-bold text-center text-dark">
                                      Delete this comment?
                                    </p>
                                    <p className="text-center text-dark">
                                      All comment replies will also be deleted.
                                    </p>
                                  </Fragment>
                                ),
                                confirmText: 'Delete Comment',
                                cancelText: 'Go back',
                                confirmBtnStyle: 'danger',
                                onConfirm: async () => {
                                  onCommentDelete(accountFileComment.id);
                                },
                                onCancel: () => {
                                  // Do nothing
                                },
                              });
                            }}
                          >
                            Delete comment
                          </button>
                        )}
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                <p
                  className="file-comment-item-message"
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{
                    __html: accountFileComment.message,
                  }}
                />
              </div>

              <div className="file-comment-item-actions">
                <span className="text-xs d-flex">
                  <RefreshingDate accountFileComment={accountFileComment} />
                </span>
                {!accountFileComment.parent?.id && (
                  <>
                    <span className="mx-1 text-xs d-flex">&#9679;</span>
                    <Button
                      onClick={onReply}
                      btnStyle="link"
                      btnSize="xs"
                      className="text-bold"
                    >
                      Reply
                    </Button>

                    {accountFileComment.children.total > 0 && (
                      <Button
                        btnStyle="reset"
                        className="file-comment-item-reply-count"
                        onClick={onViewReplies}
                      >
                        {accountFileComment.children.total}{' '}
                        {pluralize('reply', accountFileComment.children.total)}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

FileCommentItem.propTypes = propTypes;

export default FileCommentItem;
